import { DialogActions, DialogContent, DialogTitle, Divider, Stack } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { withDialogSuspenseErrorBoundary } from '@retail/components';
import { useToast } from '@retail/hooks';
import { useFetchAssortmentTags } from '@retail/my-assortment/data-access';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { AssortmentTagChanges, AssortmentTagItemsUpdate } from '@retail/my-assortment/context';
import { AssortmentItemMinimal, AssortmentTag } from '@retail/my-assortment/types';
import { EditAssortmentTagsTable } from '@retail/products/components';
import { SupplierItemAssortmentTag } from '@retail/products/types';
import { getActiveAssortmentTags } from '@retail/products/utils';
import { getISO_8601Date } from '@retail/utils';
import { ContainedButton, Dialog, OutlinedButton } from '@shared/components';
import { useCallback, useMemo } from 'react';

const toISODate = (date?: Date) => (date ? getISO_8601Date(date) : undefined);

interface Props {
  open: boolean;
  selectedItems: AssortmentItemMinimal[];
  assortmentId: number;
  isSaving?: boolean;
  updateAssortmentTags: (changes: AssortmentTagChanges) => Promise<void>;
  resetCheckedItems: () => void;
  onClose: () => void;
}

function EditAssortmentTagsDialog({
  open,
  selectedItems,
  assortmentId,
  isSaving,
  updateAssortmentTags,
  resetCheckedItems,
  onClose,
}: Props) {
  const t = useMyAssortmentTFunction();
  const toast = useToast();
  const apiRef = useGridApiRef();
  const { isBrandContext } = useSelectedOrgUnit();

  const { data: assortmentTagDtos = [] } = useFetchAssortmentTags({
    assortmentIds: [assortmentId],
  });
  const assortmentTags = useMemo(
    () => assortmentTagDtos.map(AssortmentTag.fromDTO),
    [assortmentTagDtos]
  );

  const onSubmit = useCallback(() => {
    const updatedItems = apiRef.current
      ? (Array.from(apiRef.current.getRowModels().values()) as AssortmentItemMinimal[])
      : [];

    const toTagItem = (supplierItemId: number, tag: SupplierItemAssortmentTag) =>
      isBrandContext
        ? {
            supplierItemId,
            validFrom: getISO_8601Date(tag.validFrom),
            validTo: toISODate(tag.validTo),
          }
        : { supplierItemId, validFrom: getISO_8601Date(new Date()) };

    // Only the tags the user actually touched are posted: a tag is sent for an item when it
    // was newly put on it or, at brand level, when its dates were changed. Tags taken off
    // are deleted item by item, so a save that only removes tags never reaches the
    // create/update endpoint.
    const hasChangedDates = (
      tag: SupplierItemAssortmentTag,
      persisted: SupplierItemAssortmentTag
    ) =>
      isBrandContext &&
      (toISODate(tag.validFrom) !== toISODate(persisted.validFrom) ||
        toISODate(tag.validTo) !== toISODate(persisted.validTo));

    const tagItemsByTagId = new Map<number, AssortmentTagItemsUpdate['items']>();
    const removedItemIdsByTagId = new Map<number, number[]>();

    const persistedTagsByItemId = new Map(
      selectedItems.map((item) => [
        item.mgSupplierItemId,
        new Map(getActiveAssortmentTags(item.assortmentTags).map((tag) => [tag.tagId, tag])),
      ])
    );

    updatedItems.forEach((item) => {
      const persistedTags = persistedTagsByItemId.get(item.mgSupplierItemId);
      const activeTags = getActiveAssortmentTags(item.assortmentTags);
      const activeTagIds = new Set(activeTags.map((tag) => tag.tagId));

      activeTags.forEach((tag) => {
        const persistedTag = persistedTags?.get(tag.tagId);
        if (!persistedTag || hasChangedDates(tag, persistedTag)) {
          tagItemsByTagId.set(tag.tagId, [
            ...(tagItemsByTagId.get(tag.tagId) ?? []),
            toTagItem(item.mgSupplierItemId, tag),
          ]);
        }
      });

      persistedTags?.forEach((_, tagId) => {
        if (!activeTagIds.has(tagId)) {
          removedItemIdsByTagId.set(tagId, [
            ...(removedItemIdsByTagId.get(tagId) ?? []),
            item.mgSupplierItemId,
          ]);
        }
      });
    });

    updateAssortmentTags({
      updates: Array.from(tagItemsByTagId, ([tagId, items]) => ({ tagId, items })),
      removals: Array.from(removedItemIdsByTagId, ([tagId, supplierItemIds]) => ({
        tagId,
        supplierItemIds,
      })),
    })
      .then(() => {
        resetCheckedItems();
        onClose();
      })
      .catch((e) => {
        console.error(e);
        toast.error(t('myAssortment.actions.genericActionError'));
      });
  }, [
    apiRef,
    isBrandContext,
    selectedItems,
    updateAssortmentTags,
    resetCheckedItems,
    onClose,
    toast,
    t,
  ]);

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
      <DialogTitle>{t('myAssortment.actions.editTags')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexGrow: 1, height: '70vh' }} className="padding-0">
        <EditAssortmentTagsTable
          apiRef={apiRef}
          assortmentItems={selectedItems}
          assortmentTags={assortmentTags}
          hasDatedAssortmentTags={isBrandContext}
        />
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 4, py: 3 }}>
        <Stack flexDirection="row" gap={1}>
          <OutlinedButton size="small" onClick={onClose}>
            {t('myAssortment.cancel')}
          </OutlinedButton>
          <ContainedButton size="small" loading={isSaving} onClick={onSubmit}>
            {t('myAssortment.save')}
          </ContainedButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

export default withDialogSuspenseErrorBoundary(EditAssortmentTagsDialog);
